import type { Doc } from "building-types/doc";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ElectricalRule } from "building-types/settings/electricalRuleTypes";
import { snakeCaseToTitleCase } from "common/utils/strings";
import { polyLine2EdgeCount } from "geom/polyLine2";
import { docEntityFallbackName } from "./docEntityFallbackNames";
import { docGetMeasurementShape } from "./docShapes";
import { railingGetCleanPath } from "./elements/railing/path";
import { isFloorEdgeHandle, isFloorOpeningEdgeHandle, isFoundationEdgeHandle } from "./entity/docEntityEdgeHandleTools";
import docGetEntity from "./entity/docGetEntity";
import { docGetTableEntity } from "./entity/docGetTableEntity";
import { isVirtualRoofBlock } from "./roofSystemTools";
import { formatSettingsType } from "./settingsTools";
import { isBuiltInLabel } from "./utils/builtInLabelUtils";
import { floorEdgeLabel, foundationEdgeLabel, polyLineEdgeNameAtIndex } from "./utils/edgeLabelTools";
import { electricalLayoutRuleTextSummary } from "./utils/electricalRuleUtils";
import { builtInLabelDisplayNames } from "./utils/labelTools";

export function docGetEntityName(doc: Doc, handle: DocEntityHandle | undefined): string {
  const fallbackName = docEntityFallbackName(handle);

  switch (handle?.type) {
    case "OPTION_SET":
      return docGetTableEntity(doc, handle)?.name ?? fallbackName;
    case "SETTINGS": {
      const settings = docGetTableEntity(doc, handle);

      if (!settings) {
        return fallbackName;
      }

      return settings.name || formatSettingsType(settings.type);
    }
    case "SETTINGS_SET": {
      const settingsSet = docGetTableEntity(doc, handle);
      return !!settingsSet?.name?.trim() ? settingsSet?.name : fallbackName;
    }
    case "ANNOTATION": {
      const annotation = docGetTableEntity(doc, handle);

      return annotation?.name ?? fallbackName;
    }
    case "SHAPE": {
      switch (handle.parent.type) {
        case "MEASUREMENT": {
          const mp = docGetMeasurementShape(doc, handle);
          return mp?.name ?? fallbackName;
        }

        default: {
          return "";
        }
      }
    }
    case "EDGE": {
      if (handle.parent.type === "ROOM") {
        const room = docGetTableEntity(doc, handle.parent);
        if (room?.edgeIds === undefined) {
          return fallbackName;
        }
        return "Edge " + room.edgeIds[handle.edgeId];
      }

      if (handle.parent.type === "ROOF") {
        const roof = docGetTableEntity(doc, handle.parent);

        if (!roof) {
          return fallbackName;
        }

        if (isVirtualRoofBlock(roof.blocks)) {
          return polyLineEdgeNameAtIndex(handle.edgeId, polyLine2EdgeCount(roof.blocks.perimeter)) ?? fallbackName;
        }

        // Non-virtual roofs have no sequential edge names; return "" (matches legacy EDGE→VERTEX fall-through).
        return "";
      }

      if (handle.parent.type === "RAILING") {
        const railing = docGetTableEntity(doc, handle.parent);

        if (!railing) {
          return fallbackName;
        }

        const railingCleanPath = railingGetCleanPath(railing);
        if (!railingCleanPath) {
          return "";
        }

        return polyLineEdgeNameAtIndex(handle.edgeId, polyLine2EdgeCount(railingCleanPath)) ?? "";
      }

      if (isFloorEdgeHandle(handle) || isFloorOpeningEdgeHandle(handle)) {
        const floor = docGetTableEntity(doc, handle.parent);
        if (!floor) {
          return fallbackName;
        }
        return floorEdgeLabel(floor, handle);
      }

      if (isFoundationEdgeHandle(handle)) {
        const foundation = docGetTableEntity(doc, handle.parent);
        if (!foundation) {
          return fallbackName;
        }
        return foundationEdgeLabel(foundation, handle);
      }

      // Unknown edge parent types have no display name (matches legacy EDGE→VERTEX fall-through).
      return "";
    }
    case "VERTEX": {
      if (handle.parent.type === "ROOM") {
        const room = docGetTableEntity(doc, handle.parent);

        if (!room) {
          return fallbackName;
        }

        return `${room.name}: Vertex ${handle.vertexId + 1}`;
      }
      return "";
    }
    case "ELECTRICAL_RULE": {
      const electricalRule = docGetEntity(doc, handle) as ElectricalRule;
      return electricalRule.name ?? electricalLayoutRuleTextSummary(electricalRule) ?? "";
    }
    case "ELEVATION":
      return docGetTableEntity(doc, handle)?.name ?? fallbackName;
    case "ROOF_SIDE": {
      return `${snakeCaseToTitleCase(handle.side)} Side`;
    }
    case "FLOOR_PERIMETER_OPENING": {
      return `Opening ${handle.openingIndex + 1}`;
    }
    case "AUTO_DIMENSION": {
      return "Auto Dimension";
    }
    case "LABEL": {
      if (isBuiltInLabel(handle.id)) {
        return builtInLabelDisplayNames[handle.id];
      }

      return docGetTableEntity(doc, handle)?.name ?? fallbackName;
    }
    case "EDGE_MODIFIER": {
      const edgeModifier = docGetTableEntity(doc, handle);

      if (!edgeModifier) {
        return fallbackName;
      }

      const modifier = doc.modifiers[edgeModifier.modifierId];

      if (!modifier) {
        return fallbackName;
      }

      return modifier.name ?? fallbackName;
    }
    default: {
      const entity = docGetEntity(doc, handle) as DocEntityBase;
      return !!entity?.name?.trim() ? entity?.name : fallbackName;
    }
  }
}
